import { EventEmitter } from "./EventEmitter";

export abstract class EventHandler extends EventEmitter {
    private static handlerTest: RegExp = new RegExp(`^${EventEmitter.handlerPrefix}.*`);
    private static internalHandlerTest: RegExp = new RegExp(`^__${EventEmitter.handlerPrefix}.*`);

    protected constructor() {
        super();
        // If modules are compiled for es6 (or higher) as a target, all handlers go into the prototype of the constructor.
        if (!this.registerEventHandlers()) {
            this.registerEventHandlersES6(this);
        }
    }

    dispose(): void {
        for (let key in EventHandler.handlers) {
            EventHandler.handlers[key].forEach((eventObjects) => {
                const idx = eventObjects.handlers.findIndex((x) => x.instance === this);

                if (idx > -1) {
                    eventObjects.handlers.splice(idx, 1);
                }
            });
        }
    }

    protected get internalNamespace(): string {
        return this.constructor.prototype.constructor.name;
    }

    private registerEventHandlers(): boolean {
        let handlersSet: boolean = false;

        for (let prop in this) {
            if (EventHandler.handlerTest.test(prop) && typeof this[prop] === "function") {
                let handler = prop.split("_");
                this.registerHandler(handler[0].slice(EventEmitter.handlerPrefix.length), this[prop], handler[1]);
                handlersSet = true;
            } else if (EventHandler.internalHandlerTest.test(prop) && typeof this[prop] === "function") {
                this.registerHandler(
                    prop.slice(EventEmitter.handlerPrefix.length + 2),
                    this[prop],
                    this.internalNamespace
                );
                handlersSet = true;
            }
        }

        return handlersSet;
    }

    private registerEventHandlersES6(instance: any): void {
        let instanceMethods: any = this.getMethods(instance);

        instanceMethods.forEach((method) => {
            if (EventHandler.handlerTest.test(method) && typeof instance[method] === "function") {
                let handler = method.split("_");
                this.registerHandler(handler[0].slice(EventEmitter.handlerPrefix.length), instance[method], handler[1]);
            } else if (EventHandler.handlerTest.test(method) && typeof instance[method] === "function") {
                this.registerHandler(
                    method.slice(EventEmitter.handlerPrefix.length + 2),
                    instance[method],
                    this.internalNamespace
                );
            }
        });
    }

    private registerHandler(event: string, handler: any, namespace?: string) {
        if (!namespace) {
            namespace = "global";
        }

        let handlerObject = {};
        handlerObject["handler"] = handler;
        handlerObject["instance"] = this;

        if (!EventEmitter.handlers) {
            EventEmitter.handlers = {};

            let nameSpaceObject = {};
            let eventObject = {};

            eventObject["name"] = event;
            eventObject["handlers"] = [];
            eventObject["handlers"].push(handlerObject);

            nameSpaceObject[namespace] = [];
            nameSpaceObject[namespace].push(eventObject);

            EventEmitter.handlers = nameSpaceObject;
        } else {
            let eventObjects = EventEmitter.handlers[namespace];
            if (!eventObjects) {
                eventObjects = EventEmitter.handlers[namespace] = [];
            }

            let idx = eventObjects.findIndex((x) => x.name === event);

            if (idx > -1) {
                eventObjects[idx].handlers.push(handlerObject);
            } else {
                let eventObject = {};

                eventObject["name"] = event;
                eventObject["handlers"] = [];
                eventObject["handlers"].push(handlerObject);

                eventObjects.push(eventObject);
            }
        }
    }

    private getMethods(instance) {
        let instanceProto = Object.getPrototypeOf(instance);

        if (instanceProto == null) {
            return [];
        }

        let methods = Object.getOwnPropertyNames(instanceProto);

        let res = [];

        methods.forEach((methodName) => {
            if (methodName == "constructor") {
                res = res.concat(this.getMethods(instanceProto));
            } else {
                if (!res.includes(methodName)) {
                    res.push(methodName);
                }
            }
        });

        return res;
    }
}
